/**
 * RTK Query endpoints for the daytona-service (@cluster/daytona-service).
 * Base path after the dev proxy + nginx rewrite strips `/api`: `/daytona`.
 * Sandbox resources are identified by their string `sandboxId`.
 */
import { apiSlice } from './apiSlice';
import type {
  DaytonaSandbox,
  CreateSandboxDto,
  UpdateSandboxDto,
  SandboxExecResult,
} from '@/types';

export interface SandboxListParams {
  repositoryId?: string;
  branchId?: string;
  factoryId?: string;
  ownerUserId?: string;
}

export interface SnapshotResult {
  snapshotId: string;
}

export interface SandboxFileContent {
  path: string;
  content: string;
  sizeBytes?: number;
}

/**
 * Normalize a LIST response into an array, regardless of whether the backend
 * returns a bare array `[]` or an envelope `{ data: [], pagination }`.
 * Protects `providesTags` from `e.map is not a function` shape mismatches.
 */
export function asList<T>(result: unknown): T[] {
  if (Array.isArray(result)) return result as T[];
  if (result && typeof result === 'object' && Array.isArray((result as { data?: unknown }).data)) {
    return (result as { data: T[] }).data;
  }
  return [];
}

export const daytonaApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getSandboxes: builder.query<DaytonaSandbox[], SandboxListParams | void>({
      query: (params) => ({ url: '/daytona/sandboxes', params: params || undefined }),
      providesTags: (result) => {
        const list = asList<DaytonaSandbox>(result);
        return [
          ...list.map((s) => ({ type: 'Sandbox' as const, id: s.sandboxId })),
          { type: 'Sandbox', id: 'LIST' },
        ];
      },
    }),

    getSandbox: builder.query<DaytonaSandbox, string>({
      query: (sandboxId) => `/daytona/sandboxes/${sandboxId}`,
      providesTags: (_result, _error, sandboxId) => [{ type: 'Sandbox', id: sandboxId }],
    }),

    createSandbox: builder.mutation<DaytonaSandbox, CreateSandboxDto>({
      query: (body) => ({ url: '/daytona/sandboxes', method: 'POST', body }),
      invalidatesTags: [{ type: 'Sandbox', id: 'LIST' }],
    }),

    updateSandbox: builder.mutation<DaytonaSandbox, { sandboxId: string; body: UpdateSandboxDto }>({
      query: ({ sandboxId, body }) => ({
        url: `/daytona/sandboxes/${sandboxId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, _error, { sandboxId }) =>
        result
          ? [{ type: 'Sandbox', id: sandboxId }, { type: 'Sandbox', id: 'LIST' }]
          : [{ type: 'Sandbox', id: 'LIST' }],
    }),

    deleteSandbox: builder.mutation<{ success: boolean }, string>({
      query: (sandboxId) => ({ url: `/daytona/sandboxes/${sandboxId}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'Sandbox', id: 'LIST' }],
    }),

    startSandbox: builder.mutation<DaytonaSandbox, string>({
      query: (sandboxId) => ({ url: `/daytona/sandboxes/${sandboxId}/start`, method: 'POST' }),
      invalidatesTags: (_result, _error, sandboxId) => [
        { type: 'Sandbox', id: sandboxId },
        { type: 'Sandbox', id: 'LIST' },
      ],
    }),

    stopSandbox: builder.mutation<DaytonaSandbox, string>({
      query: (sandboxId) => ({ url: `/daytona/sandboxes/${sandboxId}/stop`, method: 'POST' }),
      invalidatesTags: (_result, _error, sandboxId) => [
        { type: 'Sandbox', id: sandboxId },
        { type: 'Sandbox', id: 'LIST' },
      ],
    }),

    archiveSandbox: builder.mutation<DaytonaSandbox, string>({
      query: (sandboxId) => ({ url: `/daytona/sandboxes/${sandboxId}/archive`, method: 'POST' }),
      invalidatesTags: (_result, _error, sandboxId) => [
        { type: 'Sandbox', id: sandboxId },
        { type: 'Sandbox', id: 'LIST' },
      ],
    }),

    executeSandboxCommand: builder.mutation<
      SandboxExecResult & { sandboxId: string },
      { sandboxId: string; command: string; timeout?: number }
    >({
      query: ({ sandboxId, ...body }) => ({
        url: `/daytona/sandboxes/${sandboxId}/exec`,
        method: 'POST',
        body,
      }),
    }),

    forkSandbox: builder.mutation<
      DaytonaSandbox,
      { sandboxId: string; name?: string }
    >({
      query: ({ sandboxId, ...body }) => ({
        url: `/daytona/sandboxes/${sandboxId}/fork`,
        method: 'POST',
        body: body && Object.keys(body).length > 0 ? body : {},
      }),
      invalidatesTags: [{ type: 'Sandbox', id: 'LIST' }],
    }),

    snapshotSandbox: builder.mutation<SnapshotResult, string>({
      query: (sandboxId) => ({
        url: `/daytona/sandboxes/${sandboxId}/snapshot`,
        method: 'POST',
      }),
    }),

    getSandboxFile: builder.query<SandboxFileContent, { sandboxId: string; path: string }>({
      query: ({ sandboxId, path }) => ({
        url: `/daytona/sandboxes/${sandboxId}/files`,
        params: { path },
      }),
    }),

    refreshSandbox: builder.mutation<DaytonaSandbox, string>({
      query: (sandboxId) => ({ url: `/daytona/sandboxes/${sandboxId}/refresh`, method: 'POST' }),
      invalidatesTags: (_result, _error, sandboxId) => [{ type: 'Sandbox', id: sandboxId }],
    }),
  }),
});

// Export auto-generated hooks.
export const {
  useGetSandboxesQuery,
  useGetSandboxQuery,
  useCreateSandboxMutation,
  useUpdateSandboxMutation,
  useDeleteSandboxMutation,
  useStartSandboxMutation,
  useStopSandboxMutation,
  useArchiveSandboxMutation,
  useExecuteSandboxCommandMutation,
  useForkSandboxMutation,
  useSnapshotSandboxMutation,
  useGetSandboxFileQuery,
  useRefreshSandboxMutation,
} = daytonaApi;